import {
  ACCESS_ROLES,
  AccessRole,
  DEFAULT_HMI_ACCESS_ROLE,
  ROLE_PERMISSION,
} from 'src/app/app.constants';

export type SchemeValidation = {
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  defaultValue?: number | boolean | string | object | any[] | null;
  isArray?: boolean;
  children?: Record<
    string,
    {
      defaultValue?: any;
      validation?: SchemeValidation;
      apiToGui?: (v: any) => any;
      guiToApi?: (v: any) => any;
      fieldName?: string;
      fieldUnit?: string;
    }
  >;
};

export type SchemeLeaf = {
  validation: SchemeValidation;
  apiToGui: (value: any) => any;
  guiToApi: (value: any) => any;
  /** Minimum role required to edit this field.
   *  Omit to leave the field editable from Full R/W down. */
  accessLevel?: Exclude<AccessRole, typeof ACCESS_ROLES.READONLY>;
  fieldName?: string;
  fieldUnit?: string;
};

export type SchemeGroup = Record<
  string,
  SchemeLeaf | Record<string, SchemeLeaf | Record<string, SchemeLeaf>>
>;

type WellScheme = Record<string, SchemeGroup | SchemeLeaf>;

/**
 * Decodes a stored panel password. An unencoded one reads as no password, so the well still loads.
 */
function decodePassword(value: string | null): string | null {
  if (!value) return value;
  try {
    const decoded = decodeURIComponent(atob(value));
    // Most unencoded PINs are valid Base64 too, but only an encoded password will round-trip.
    return btoa(encodeURIComponent(decoded)) === value ? decoded : null;
  } catch {
    return null;
  }
}

export const WELL_SCHEME: WellScheme = {
  // ── Root-level well fields (no nesting in the API object) ───────────────────
  allowedUserGroups: {
    validation: {
      required: true,
      defaultValue: [],
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    fieldName: 'Groups',
  },
  dayStartOffset: {
    validation: {
      required: true,
      defaultValue: 21600000, // 6 hours in milliseconds
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    fieldName: '24-Hour Production Start Time',
  },
  displayName: {
    validation: {
      required: true,
      // Cannot contain: % & ? / \ = or tabs or 2 or more consecutive spaces and cannot start or end with a space and length between 3 and 50
      pattern: `^(?!.*[%&?\/\\\\=])(?!.*[\\t])(?!.* {2,})(?! )(?!.* $)[\\s\\S]{3,50}$`,
      minLength: 3,
      maxLength: 50,
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    accessLevel: ACCESS_ROLES.SUPER_ADMIN,
    fieldName: 'Well Name',
  },
  liftType: {
    validation: {
      required: true,
      defaultValue: 1,
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    fieldName: 'Artificial Lift Type',
  },
  name: {
    validation: {
      defaultValue: null,
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    fieldName: 'Database Username',
  },
  password: {
    validation: {
      required: true,
      defaultValue: 'libertypass',
      pattern: `^[a-zA-Z0-9_\`-]{4,40}$`,
      minLength: 4,
      maxLength: 40,
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    accessLevel: ACCESS_ROLES.SUPER_ADMIN,
    fieldName: 'Password',
  },
  tags: {
    validation: {
      isArray: true,
      defaultValue: [],
      required: true,
      minLength: 3,
      maxLength: 50,
      pattern: '^[^%&?/\\\\=]*$',
    },
    apiToGui: (value) => value,
    guiToApi: (value) => value,
    fieldName: 'Tags (Optional)',
  },
  timeZone: {
    validation: {
      required: true,
      defaultValue: 'America/North_Dakota/Center',
    },
    apiToGui: (value) => (isNaN(value) ? value : 'America/North_Dakota/Center'),
    guiToApi: (value) => value,
    fieldName: 'Well Time Zone',
  },
  // ── Nested groups ────────────────────────────────────────────────────────────
  casing: {
    innerDiameters: {
      validation: {
        required: true,
        min: 3.2,
        max: 12.8,
        isArray: true,
        defaultValue: [4.892],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    lengths: {
      validation: {
        required: true,
        min: 1,
        max: 20000,
        isArray: true,
        defaultValue: [8000],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    sfCasingGasInnerPressureDefault: {
      validation: {
        required: true,
        min: 0,
        max: 3000,
        defaultValue: 112,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Casing Pressure',
      fieldUnit: 'psi',
    },
  },
  casingGasClaimConfig: {
    avHearingDuration: {
      validation: { defaultValue: 20 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    flHearingDuration: {
      validation: { defaultValue: 1 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
  },
  channels: {
    analog: {
      name: {
        validation: {
          required: true,
          defaultValue: '',
          maxLength: 50,
          // Cannot contain: % & ? / \ = or tabs or 2 or more consecutive spaces and cannot start or end with a space.
          pattern: `^(?!.*[%&?\/\\\\=])(?!.*[\\t])(?!.* {2,})(?! )(?!.* $)[\\s\\S]{1,50}$`,
        },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      signalConfig: {
        signalRange: {
          validation: { required: true, defaultValue: null },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
        },
        unit: {
          validation: { required: true, defaultValue: null },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
        },
        processMin: {
          validation: { required: true, min: -9999999, max: 9999999, defaultValue: 0 },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
        },
        processMax: {
          validation: { required: true, min: -9999999, max: 9999999, defaultValue: 0 },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
        },
        signalsRangeMin: {
          validation: { required: true, min: -9999999, max: 9999999, defaultValue: 0 },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
        },
        signalsRangeMax: {
          validation: { required: true, min: -9999999, max: 9999999, defaultValue: 0 },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
        },
      },
    },
    digital: {
      name: {
        validation: {
          required: true,
          defaultValue: '',
          maxLength: 50,
          // Cannot contain: % & ? / \ = or tabs or 2 or more consecutive spaces and cannot start or end with a space.
          pattern: `^(?!.*[%&?\/\\\\=])(?!.*[\\t])(?!.* {2,})(?! )(?!.* $)[\\s\\S]{1,50}$`,
        },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => (value ? 1 : 0),
      },
    },
    intScaledVirtual: { validation: {}, apiToGui: (value) => value, guiToApi: (value) => value },
  },
  dynoConfig: {
    posnPeakThresholdRate: {
      validation: { required: true, defaultValue: 0.05, min: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Position Peak Threshold Rate',
    },
    strokeEndCrankTicksCount: {
      validation: { required: true, defaultValue: 3, min: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Crank Ticks Count',
      fieldUnit: 'ticks',
    },
    posnPeakSmoothingWindowSize: {
      validation: { required: true, defaultValue: 20 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Position Peak Smoothing Window Size',
    },
    strokeMaxDuration: {
      validation: { required: true, defaultValue: 40 },
      apiToGui: (value) => value / 1000,
      guiToApi: (value) => value * 1000,
      fieldName: 'Minimum SPM Duration',
      fieldUnit: 'sec',
    },
    strokeMinLengthRate: {
      validation: { required: true, defaultValue: 90 },
      apiToGui: (value) => value * 100,
      guiToApi: (value) => value / 100,
      fieldName: 'Stroke Min Length Rate',
      fieldUnit: '%',
    },
    strokeEndDetectionType: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Stroke End Detection Type',
    },
  },
  grips: {
    bandMaxFillage: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
    },
    bandMaxPIP: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
    },
    bandMinFillage: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
    },
    bandMinPIP: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
    },
    maxLoad: {
      allowedViolationsCount: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Peak Load Retries Allowed',
      },
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
        fieldName: 'Peak Load',
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Peak Load Retries Time',
      },
      threshold: {
        validation: { required: true, min: 0, defaultValue: 35000 },
        apiToGui: (value) => value * 1000,
        guiToApi: (value) => value / 1000,
        fieldName: 'Peak Load Violation Set Point',
        fieldUnit: 'lbs',
      },
      thresholdDuration: {
        validation: { required: true, min: 0, defaultValue: 2 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Peak Load Violation',
        fieldUnit: 'sec',
      },
    },
    minFillage: {
      isActive: {
        validation: { required: true, defaultValue: true },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Off Time Duration',
      },
      threshold: {
        validation: { required: true, min: 0, max: 100, defaultValue: 70 },
        apiToGui: (value) => +(value * 100).toFixed(0),
        guiToApi: (value) => value / 100,
        fieldName: 'Fillage Set Point',
        fieldUnit: '%',
      },
      thresholdTicks: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Pump Off Strokes Allowed',
        fieldUnit: 'strokes',
      },
    },
    minFluidLoad: {
      allowedViolationsCount: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Low Fluid Load Retries Allowed',
      },
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
        fieldName: 'Low Fluid Load',
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Low Fluid Load Retries Time',
      },
      threshold: {
        validation: { required: true, min: 0, defaultValue: 2000 },
        apiToGui: (value) => value * 1000,
        guiToApi: (value) => value / 1000,
        fieldName: 'Low Fluid Load Violation Set Point',
        fieldUnit: 'lbs',
      },
      thresholdTicks: {
        validation: { required: true, min: 1, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Low Fluid Load Violation',
        fieldUnit: 'strokes',
      },
    },
    minLoad: {
      allowedViolationsCount: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Minimum Load Retries Allowed',
      },
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
        fieldName: 'Minimum Load',
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Minimum Load Retries Time',
      },
      threshold: {
        validation: { required: true, min: 0, defaultValue: 5000 },
        apiToGui: (value) => value * 1000,
        guiToApi: (value) => value / 1000,
        fieldName: 'Minimum Load Violation Set Point',
        fieldUnit: 'lbs',
      },
      thresholdDuration: {
        validation: { required: true, min: 0, defaultValue: 2 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Minimum Load Violation',
        fieldUnit: 'sec',
      },
    },
    minPIP: {
      isActive: {
        validation: { required: true, defaultValue: true },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Off Time Duration',
      },
      threshold: {
        validation: { required: true, min: 0, max: 10000, defaultValue: 100 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'PIP Set Point',
        fieldUnit: 'psi',
      },
      thresholdTicks: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Pump Off Strokes Allowed',
        fieldUnit: 'strokes',
      },
    },
    oddSFCard: {
      allowedViolationsCount: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Malfunction Point Retries Allowed',
      },
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
        fieldName: 'Malfunction Point',
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Malfunction Point Retries Time',
      },
      threshold: {
        load: {
          validation: { required: true, min: 0, defaultValue: 0 },
          apiToGui: (value) => value * 1000,
          guiToApi: (value) => value / 1000,
          fieldName: 'Malfunction Point Load Violation Set Point ',
          fieldUnit: 'lbs',
        },
        posn: {
          validation: { required: true, min: 0, defaultValue: 0 },
          apiToGui: (value) => value,
          guiToApi: (value) => value,
          fieldName: 'Malfunction Point Position Violation Set Point',
          fieldUnit: 'in',
        },
      },
      thresholdTicks: {
        validation: { required: true, min: 1, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Malfunction Point Violation',
        fieldUnit: 'strokes',
      },
    },
    plungerVelocityACM: {
      edgeTrim: {
        validation: { required: true, min: 0, max: 49, defaultValue: 5 },
        apiToGui: (value) => value * 100,
        guiToApi: (value) => value / 100,
        fieldName: 'Edge Trim',
        fieldUnit: '%',
      },
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
        fieldName: 'Plunger Velocity ACM',
      },
      threshold: {
        validation: { required: true, min: -2, max: 0, defaultValue: -1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Threshold',
        fieldUnit: 'ft/s',
      },
    },
    secondaryMinFillage: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      threshold: {
        validation: { required: true, min: 0, defaultValue: 50 },
        apiToGui: (value) => +(value * 100).toFixed(0),
        guiToApi: (value) => value / 100,
        fieldName: 'Secondary Fillage Set Point',
        fieldUnit: '%',
      },
      thresholdTicks: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    secondaryMinPIP: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      threshold: {
        validation: { required: true, min: 0, defaultValue: 0 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Secondary PIP Set Point',
        fieldUnit: 'psi',
      },
      thresholdTicks: {
        validation: { required: true, min: 0, defaultValue: 3 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    spmPerHzCalibration: {
      threshold: {
        validation: { defaultValue: null },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldUnit: 'Hz',
      },
    },
    timer: {
      isActive: {
        validation: { required: true, defaultValue: false },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
      },
      motorOffDuration: {
        validation: { required: true, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Off Time',
      },
      thresholdDuration: {
        validation: { required: true, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'On Time',
      },
    },
    unableToStart: {
      allowedViolationsCount: {
        validation: { required: true, min: 0, defaultValue: 0 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Position Fault Retries Allowed',
      },
      isActive: {
        validation: { required: true, defaultValue: true },
        apiToGui: (value) => !!value,
        guiToApi: (value) => +value,
        fieldName: 'Position Fault',
      },
      motorOffDuration: {
        validation: { required: true, min: 0, defaultValue: 1200 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Position Fault Retries Allowed',
      },
      thresholdDuration: {
        validation: { required: true, min: 1, defaultValue: 10 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
        fieldName: 'Position Fault Violation',
        fieldUnit: 'sec',
      },
    },
    unexpectedAnalog: {
      validation: { defaultValue: [] },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    unexpectedDigital: {
      validation: { defaultValue: [] },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
  },
  /** Per-item form schema for the io-analog malfunction editor — not an API field. */
  analogMalfunctionItem: {
    isActive: {
      validation: { required: true, defaultValue: false },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
    },
    thresholdType: {
      validation: { required: true, defaultValue: null },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    threshold: {
      validation: { required: true, defaultValue: null },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    thresholdDuration: {
      validation: { required: true, min: 0, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    motorOffDuration: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    allowedViolationsCount: {
      validation: { required: true, min: 0, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
  },
  /** Per-item form schema for the io-digital malfunction editor — not an API field. */
  digitalMalfunctionItem: {
    isActive: {
      validation: { required: true, defaultValue: false },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
    },
    threshold: {
      validation: { required: true, defaultValue: null },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    thresholdDuration: {
      validation: { required: true, min: 0, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    motorOffDuration: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    allowedViolationsCount: {
      validation: { required: true, min: 0, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
  },
  hmi: {
    defaultPermission: {
      validation: { required: true, defaultValue: ROLE_PERMISSION[DEFAULT_HMI_ACCESS_ROLE] },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      accessLevel: ACCESS_ROLES.USER_ADMIN,
      fieldName: 'Default HMI Access Role',
    },
    userAccessGroup: {
      validation: {
        isArray: true,
        children: {
          userName: {
            defaultValue: 'everyone',
            validation: {
              required: true,
              // Can contain: numbers and letters or the special symbols  [. | _ | -], can not contain spaces  and the  length has to be between 5 and 50
              pattern: `^[a-zA-Z0-9._-]{5,50}$`,
              minLength: 5,
              maxLength: 50,
            },
            apiToGui: (value) => value,
            guiToApi: (value) => value,
            fieldName: 'User Name',
          },
          password: {
            defaultValue: null,
            validation: {
              required: true,
              // Can contain: numbers and letters, can not contain spaces  and the  length has to be between 4 and 12
              pattern: `^[a-zA-Z0-9]{4,12}$`,
              minLength: 4,
              maxLength: 12,
            },
            apiToGui: decodePassword,
            guiToApi: (value: string | null) => (value ? btoa(encodeURIComponent(value)) : value),
            fieldName: 'Password / PIN',
          },
          permission: {
            defaultValue: ROLE_PERMISSION[ACCESS_ROLES.READONLY],
            validation: { required: true },
            apiToGui: (value) => value,
            guiToApi: (value) => value,
            fieldName: 'Role',
          },
        },
        defaultValue: [],
      },
      apiToGui: (items: any[]) =>
        items?.map((item) => ({
          ...item,
          password: decodePassword(item.password),
        })),
      guiToApi: (items: any[]) =>
        items?.map((item) => ({
          ...item,
          password: item.password ? btoa(encodeURIComponent(item.password)) : item.password,
        })),
      accessLevel: ACCESS_ROLES.USER_ADMIN,
    },
  },
  jack: {
    dimA: {
      validation: {
        required: true,
        defaultValue: 300,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Pumping Unit Dimension A',
      fieldUnit: 'in',
    },
    maxAllowedDimA: {
      validation: {
        required: true,
        defaultValue: 500,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Maximum Allowed Dimension A',
      fieldUnit: 'in',
    },
    posnOffset: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Position Offset',
      fieldUnit: 'in',
    },
    measuredStrokeLength: {
      validation: { required: true, defaultValue: 160, min: 1, max: 500 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Measured Stroke Length',
      fieldUnit: 'in',
    },
    // Not user-editable — SurfaceEquipmentCardComponent keeps this in sync with
    // measuredStrokeLength whenever it changes, so both are sent together on save.
    canonicalStrokeLength: {
      validation: { required: true, defaultValue: 160, min: 1, max: 500 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    stuffingBoxFriction: {
      validation: { required: true, defaultValue: 100, min: 0, max: 1000 },
      apiToGui: (value) => +(value * 1000).toFixed(4),
      guiToApi: (value) => value / 1000,
      fieldName: 'Stuffing Box Friction',
      fieldUnit: 'lbs',
    },
    topLoad: {
      validation: { required: true, defaultValue: 30500, min: 1, max: 100000 },
      apiToGui: (value) => +(value * 1000).toFixed(4),
      guiToApi: (value) => value / 1000,
      fieldName: 'Unit Structure Rating',
      fieldUnit: 'lbs',
    },
    gearboxRatedTorque: {
      validation: { required: true, defaultValue: 640, min: 1, max: 10000 },
      apiToGui: (value) => +(value / 1000).toFixed(4),
      guiToApi: (value) => value * 1000,
      fieldName: 'Gearbox Rated Torque',
      fieldUnit: 'k in-lbs',
    },
    gearboxLinkageEfficiency: {
      validation: { required: true, defaultValue: 100, min: 1, max: 100 },
      apiToGui: (value) => +(value * 100).toFixed(2),
      guiToApi: (value) => value / 100,
      fieldName: 'Gearbox Linkage Efficiency',
      fieldUnit: '%',
    },
    useSimulatedStrokeLength: {
      validation: { required: true, defaultValue: false },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
    },
  },
  location: {
    latitude: {
      validation: { required: true, min: -90, max: 90, defaultValue: 31.9193497 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Latitude',
      fieldUnit: 'DD',
    },
    longitude: {
      validation: { required: true, min: -180, max: 180, defaultValue: -102.192879 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Longitude',
      fieldUnit: 'DD',
    },
  },
  machineConfig: {
    wellUnitConfig: {
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    sensorsDataUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: {
          required: true,
          defaultValue: 1,
        },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    hoaStateUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    motorStateUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    casingValveStateUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    dynoUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      updatePeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    echoUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      updatePeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    strokeUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyCount: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    swellUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyCount: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    shotUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyCount: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    buildupUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyCount: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    casingGasDataUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    deviationUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    violationUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    malfunctionUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    mediationUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    casingGasClaimUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyCount: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    controlUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    operationalStateUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    casingGasStateUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
    modbusMapUnitConfig: {
      isActive: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      historyDepth: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      supplyPeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
      updatePeriod: {
        validation: { required: true, defaultValue: 1 },
        apiToGui: (value) => value,
        guiToApi: (value) => value,
      },
    },
  },
  mixture: {
    acousticVelocityLooseMin: {
      validation: { defaultValue: 800 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    atmosphericPressure: {
      validation: {
        required: true,
        min: 12,
        max: 15,
        defaultValue: 14.7,
      },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Atmospheric Pressure',
      fieldUnit: 'psi',
    },
    calculateFluidBubblingGor: {
      validation: { required: true, defaultValue: true },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
      fieldName: 'Automatic Solution GOR',
    },
    calculateFluidBubblingPressure: {
      validation: { required: true, defaultValue: true },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
      fieldName: 'Automatic Bubble Point Pressure',
    },
    calculateLiquidPressureGradient: {
      validation: { required: true, defaultValue: true },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
      fieldName: 'Automatic Tubing Gradient',
    },
    calculateOilFormationFactor: {
      validation: { required: true, defaultValue: true },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
      fieldName: 'Automatic Formation Volume Factor',
    },
    fluidBubblingGor: {
      validation: { required: true, min: 10, max: 1500, defaultValue: 800 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Solution GOR',
      fieldUnit: 'scf.gas',
    },
    fluidBubblingPressure: {
      validation: {
        required: true,
        min: 30,
        max: 4000,
        defaultValue: 2000,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Bubble Point Pressure',
      fieldUnit: 'psi',
    },
    fluidDampingCoefficient: {
      validation: { required: true, defaultValue: 0.01 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Damping Factor',
    },
    fluidFrictionRatio: {
      validation: { required: true, min: 0, max: 100, defaultValue: 11.2 },
      apiToGui: (value) => +(value * 100).toFixed(1),
      guiToApi: (value) => +(value / 100),
      fieldName: 'Mechanical Friction',
      fieldUnit: '%',
    },
    fluidViscosity: {
      validation: { required: true, min: 0, defaultValue: 1 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Fluid Viscosity',
      fieldUnit: 'cP',
    },
    gasSpecificGravity: {
      validation: {
        required: true,
        min: 0.45,
        max: 2,
        defaultValue: 0.55,
      },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Gas Specific Gravity',
    },
    liquidPressureGradient: {
      validation: {
        required: true,
        min: 0,
        max: 0.7,
        defaultValue: 0.4,
      },
      apiToGui: (value) => +value.toFixed(3),
      guiToApi: (value) => value,
      fieldName: 'Tubing Gradient',
      fieldUnit: 'psi/ft',
    },
    oilApiGravity: {
      validation: { required: true, min: 10, max: 50, defaultValue: 40 },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Oil API gravity',
      fieldUnit: 'deg',
    },
    oilFormationFactor: {
      validation: {
        required: true,
        min: 1.05,
        max: 1.75,
        defaultValue: 1.5,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Formation Volume Factor',
      fieldUnit: 'rb/stb',
    },
    serviceFactor: {
      validation: {
        required: true,
        min: 0.5,
        max: 1,
        defaultValue: 0.9,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Service Factor',
    },
    sfTemperature: {
      validation: {
        required: true,
        min: 40,
        max: 200,
        defaultValue: 75,
      },
      apiToGui: (value) => +(value - 459.67).toFixed(4),
      guiToApi: (value) => value + 459.67,
      fieldName: 'Surface Temperature',
      fieldUnit: '°F',
    },
    sfWaterCut: {
      validation: { required: true, min: 0, max: 100, defaultValue: 68 },
      apiToGui: (value) => value * 100,
      guiToApi: (value) => value / 100,
      fieldName: 'Water Cut',
      fieldUnit: '%',
    },
    temperatureGradient: {
      validation: {
        required: true,
        min: 0.2,
        max: 4.5,
        defaultValue: 0.8,
      },
      apiToGui: (value) => +(value * 100).toFixed(4),
      guiToApi: (value) => value / 100,
      fieldName: 'Temperature Gradient',
      fieldUnit: '°F/100 ft',
    },
    waterSpecificGravity: {
      validation: { required: true, min: 0.9, max: 1.5, defaultValue: 1.01 },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Water Specific Gravity',
    },
  },
  motor: {
    inverterRatedPower: {
      validation: { required: true, min: 1, max: 500, defaultValue: 75 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Inverter Rated Power',
      fieldUnit: 'hp',
    },
    motorRatedPower: {
      validation: { required: true, min: 1, max: 500, defaultValue: 75 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Motor Rated Power',
      fieldUnit: 'hp',
    },
  },
  operationalStateConfig: {
    buzzingDuration: {
      validation: { required: true, defaultValue: 10, min: 0 },
      apiToGui: (value) => value / 1000,
      guiToApi: (value) => value * 1000,
      fieldName: 'Buzzing Duration',
      fieldUnit: 'sec',
    },
    startingTimeout: {
      validation: { required: true, defaultValue: 25, min: 0 },
      apiToGui: (value) => value / 1000,
      guiToApi: (value) => value * 1000,
      fieldName: 'Starting Timeout',
      fieldUnit: 'sec',
    },
    stoppingTimeout: {
      validation: { required: true, defaultValue: 60, min: 0 },
      apiToGui: (value) => value / 1000,
      guiToApi: (value) => value * 1000,
      fieldName: 'Stopping Timeout',
      fieldUnit: 'sec',
    },
  },
  pump: {
    clearance: {
      validation: { required: true, defaultValue: 0.006, min: 0 },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Pump Diameter Clearance',
      fieldUnit: 'in',
    },
    depth: {
      validation: { required: true, defaultValue: 8000, min: 0, max: 20000 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'True Vertical Pump Depth (TVD)',
      fieldUnit: 'ft',
    },
    diameter: {
      validation: { required: true, defaultValue: 1.75, min: 0 },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Pump Plunger Diameter',
      fieldUnit: 'in',
    },
    distance: {
      validation: { required: true, defaultValue: 8000, min: 1, max: 20000 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Measured Pump Depth (MD)',
      fieldUnit: 'ft',
    },
    kellyBushingHeight: {
      validation: {
        required: true,
        min: 0,
        max: 50,
        defaultValue: 25,
      },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
    },
    length: {
      validation: { required: true, defaultValue: 60, min: 0 },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Pump Plunger Length',
      fieldUnit: 'in',
    },
  },
  rodString: {
    diameters: {
      validation: {
        required: true,
        min: 0.25,
        max: 4,
        isArray: true,
        defaultValue: [1],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Diameter',
      fieldUnit: 'in',
    },
    lengths: {
      validation: {
        required: true,
        min: 1,
        max: 65535,
        isArray: true,
        defaultValue: [8000],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Length',
      fieldUnit: 'ft',
    },
    linearWeights: {
      validation: {
        required: true,
        min: 0.01,
        max: 30,
        isArray: true,
        defaultValue: [2.89],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Weight Per Foot',
      fieldUnit: 'lb/ft',
    },
    rodTypes: {
      validation: {
        required: true,
        isArray: true,
        defaultValue: [0],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Rod Type',
    },
    stiffnesses: {
      validation: {
        required: true,
        min: 4,
        max: 40,
        isArray: true,
        defaultValue: [30.5],
      },
      apiToGui: (value) => {
        const arr = [];
        const l = value.length;
        for (let i = 0; i < l; i++) {
          arr.push(value[i] / 1000000);
        }
        return arr;
      },
      guiToApi: (value) => {
        const arr = [];
        const l = value.length;
        for (let i = 0; i < l; i++) {
          arr.push(value[i] * 1000000);
        }
        return arr;
      },
      fieldName: 'Modulus',
      fieldUnit: 'Mpsi',
    },
    stressVelocityAdjustmentCoefficient: {
      validation: { required: true, min: 0.1, max: 2, defaultValue: 0.95 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Sound Speed Couplings Adjustment',
    },
  },
  shotConfig: {
    pulseDetectionPulseDistance: {
      validation: {
        defaultValue: null,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
  },
  strokeConfig: {
    dayStartOffset: {
      validation: {
        required: true,
        defaultValue: 21600000,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
    fillBaseRatio: {
      validation: { required: true, min: 0, max: 100, defaultValue: 45 },
      apiToGui: (value) => value * 100,
      guiToApi: (value) => value / 100,
      fieldName: 'Fillbase',
      fieldUnit: '%',
    },
    markedStrokeAdjacencyCount: {
      validation: {
        required: true,
        defaultValue: 5,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Marked Stroke Adjacency Count',
      fieldUnit: 'strokes',
    },
    markedStrokeStandardPeriod: {
      validation: {
        required: true,
        defaultValue: 3600,
      },
      apiToGui: (value) => value / 1000,
      guiToApi: (value) => value * 1000,
      fieldName: 'Marked Stroke Standard Period',
      fieldUnit: 'sec',
    },
    minFillageStartupStrokesCount: {
      validation: { required: true, min: 0, defaultValue: 3 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Start Up Strokes',
      fieldUnit: 'strokes',
    },
    minPIPStartupStrokesCount: {
      validation: { required: true, min: 0, defaultValue: 3 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Start Up Strokes',
      fieldUnit: 'strokes',
    },
    pdpCalculationAccuracy: {
      validation: {
        required: true,
        defaultValue: 0.1,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Pressure Calculation Accuracy',
      fieldUnit: 'psi',
    },
    pdpCalculationStepsMax: {
      validation: {
        required: true,
        defaultValue: 1000,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Pressure Calculation Iterations Limit',
      fieldUnit: 'times',
    },
    useKneePoint: {
      validation: { required: true, defaultValue: 1 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Net Stroke Calculation Method',
    },
    dhSize: {
      validation: { required: true, defaultValue: 200 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'DH Card Points',
      fieldUnit: 'points',
    },
    sfSize: {
      validation: { required: true, min: 200, max: 1000, defaultValue: 1000 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'SF Card Points',
      fieldUnit: 'points',
    },
    sf2dhFourierCoefficientsCount: {
      validation: { required: true, defaultValue: 14 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Number of Fourier Coefficients',
    },
  },
  motorStateConfig: {
    onOffDetectionType: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Motor On/Off Detection Type',
    },
  },
  survey: {
    azimuth: {
      validation: {
        isArray: true,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Azimuth',
      fieldUnit: 'deg',
    },
    inclination: {
      validation: {
        isArray: true,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Inclination',
      fieldUnit: 'deg',
    },
    measuredDepth: {
      validation: {
        isArray: true,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Measured Depth',
      fieldUnit: 'ft',
    },
    sideLoadHighHighLimit: {
      validation: { required: true, min: 0, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Side Load Severe Limit',
      fieldUnit: 'lbs',
    },
    sideLoadHighLimit: {
      validation: { required: true, min: 0, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Side Load Moderate Limit',
      fieldUnit: 'lbs',
    },
    size: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
    },
  },
  tubing: {
    anchorDepth: {
      validation: { required: true, min: 1000, max: 20000, defaultValue: 8000 },
      apiToGui: (value) => +value.toFixed(4),
      guiToApi: (value) => value,
      fieldName: 'Anchor Depth',
      fieldUnit: 'ft',
    },
    innerDiameters: {
      validation: {
        required: true,
        min: 0.25,
        max: 10,
        isArray: true,
        defaultValue: [2.441],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Inner Diameter',
      fieldUnit: 'in',
    },
    isAnchored: {
      validation: {
        required: true,
        defaultValue: true,
      },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
      fieldName: 'Is Tubing Anchored?',
    },
    jointCounts: {
      validation: {
        required: true,
        min: 30,
        max: 700,
        isArray: true,
        defaultValue: [200],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Tubing Joints',
    },
    lengths: {
      validation: {
        required: true,
        min: 20,
        max: 20000,
        isArray: true,
        defaultValue: [8000],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Length',
      fieldUnit: 'ft',
    },
    outerDiameters: {
      validation: {
        required: true,
        min: 0.25,
        max: 10,
        isArray: true,
        defaultValue: [2.875],
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Outer Diameter',
      fieldUnit: 'in',
    },
    sfTubingFluidInnerPressureDefault: {
      validation: {
        required: true,
        min: 0,
        max: 3000,
        defaultValue: 300,
      },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Tubing Head Pressure',
      fieldUnit: 'psi',
    },
  },
  vfd: {
    constantSpeed: {
      validation: { required: true, min: 0, max: 120, defaultValue: 60 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Constant speed',
    },
    deadBandRate: {
      validation: { required: true, min: 0, max: 100, defaultValue: 3 },
      apiToGui: (value) => +(value * 100).toFixed(4),
      guiToApi: (value) => value / 100,
      fieldName: 'Deadband +/-',
      fieldUnit: '%',
    },
    deadBandTicks: {
      validation: { required: true, min: 0, max: 1000, defaultValue: 1 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Dead band strokes',
      fieldUnit: 'strokes',
    },
    downstrokeZones: {
      validation: {
        required: true,
        isArray: true,
        children: {
          posnRate: {
            defaultValue: null,
            validation: { required: true, min: 5, max: 95 },
            apiToGui: (v) => +(v * 100).toFixed(0),
            guiToApi: (v) => v / 100,
          },
          speedRate: {
            defaultValue: null,
            validation: { required: true, min: 0, max: 120 },
            apiToGui: (v) => +(v * 100).toFixed(0),
            guiToApi: (v) => v / 100,
          },
        },
        defaultValue: [
          { posnRate: 80, speedRate: 100 },
          { posnRate: 20, speedRate: 100 },
        ],
      },
      apiToGui: (value: any[]) =>
        value?.map((z) => ({
          posnRate: +(z.posnRate * 100).toFixed(0),
          speedRate: +(z.speedRate * 100).toFixed(0),
        })),
      guiToApi: (value: any[]) =>
        value?.map((z) => ({ posnRate: z.posnRate / 100, speedRate: z.speedRate / 100 })),
    },
    driveType: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Drive Type',
    },
    spmPerHzFactor: {
      // Not required: only known once the well is calibrated post-creation (see isSPMDisabled),
      // and the only editable input for it (developer SPM/Hz Conversion card) isn't part of the
      // Add Well wizard, so a required validator here could never be satisfied for a new well.
      validation: {},
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'k (spmPerHzFactor)',
    },
    spmPerHzOffset: {
      validation: {},
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'b (spmPerHzOffset)',
    },
    spmPerHzCalibrationStrokeViabilityFactor: {
      validation: { required: true, defaultValue: 1.5 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'vfd.spmPerHzCalibration StrokeViabilityFactor',
    },
    spmPerHzCalibrationTimeoutTicks: {
      validation: { required: true, defaultValue: 60 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'vfd.spmPerHzCalibration TimeoutTicks',
    },
    speedDecrease: {
      validation: { required: true, min: 0, max: 120, defaultValue: 1 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Speed decrease',
    },
    speedIncrease: {
      validation: { required: true, min: 0, max: 120, defaultValue: 1 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Speed increase',
    },
    speedMax: {
      validation: { required: true, min: 0, max: 120, defaultValue: 60 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Peak Working Speed',
    },
    speedMin: {
      validation: { required: true, min: 0, max: 120, defaultValue: 40 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Min Working Speed',
    },
    startupSpeed: {
      validation: { required: true, min: 0, max: 120, defaultValue: 60 },
      apiToGui: (value) => value,
      guiToApi: (value) => value,
      fieldName: 'Start Up Speed',
    },
    upstrokeZones: {
      validation: {
        required: true,
        isArray: true,
        children: {
          posnRate: {
            defaultValue: null,
            validation: { required: true, min: 5, max: 95 },
            apiToGui: (v) => +(v * 100).toFixed(0),
            guiToApi: (v) => v / 100,
          },
          speedRate: {
            defaultValue: null,
            validation: { required: true, min: 0, max: 120 },
            apiToGui: (v) => +(v * 100).toFixed(0),
            guiToApi: (v) => v / 100,
          },
        },
        defaultValue: [
          { posnRate: 20, speedRate: 100 },
          { posnRate: 80, speedRate: 100 },
        ],
      },
      apiToGui: (value: any[]) =>
        value?.map((z) => ({
          posnRate: +(z.posnRate * 100).toFixed(0),
          speedRate: +(z.speedRate * 100).toFixed(0),
        })),
      guiToApi: (value: any[]) =>
        value?.map((z) => ({ posnRate: z.posnRate / 100, speedRate: z.speedRate / 100 })),
    },
    use: {
      validation: { required: true, defaultValue: false },
      apiToGui: (value) => !!value,
      guiToApi: (value) => +value,
      fieldName: 'Enable VFD',
    },
    useZones: {
      validation: { required: true, defaultValue: 0 },
      apiToGui: (value) => +value,
      guiToApi: (value) => +value,
      fieldName: 'Enable Speed Zones',
    },
  },
  // Calibration map values for inclinometer and loadcell — stored in channels.analog[i].map.map1 where name contains the channel name
  inclinometerLoadcellConfig: {
    inclinometerSockety1: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'ADC Min Value',
    },
    inclinometerSockety2: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'ADC Max Value',
    },
    loadCellSockety1: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'ADC Min Value',
    },
    loadCellSockety2: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'ADC Max Value',
    },
  },
  // Register addresses for VFD virtual channels — stored in channels.intScaledVirtual[n].reg.address
  vfdChannelAddresses: {
    vfdAmbientTemperatureAddress: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'VFD Ambient Temperature',
      fieldUnit: 'registry #',
    },
    vfdDCBusVoltageAddress: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'VFD DC Bus Voltage',
      fieldUnit: 'registry #',
    },
    vfdMotorCurrentAddress: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'VFD Motor Current',
      fieldUnit: 'registry #',
    },
    vfdMotorVoltageAddress: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'VFD Motor Voltage',
      fieldUnit: 'registry #',
    },
    vfdOutFrequencyAddress: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'VFD Out Frequency',
      fieldUnit: 'registry #',
    },
    vfdMotorTorqueAddress: {
      validation: { required: true, defaultValue: null },
      apiToGui: (v) => v,
      guiToApi: (v) => v,
      fieldName: 'VFD Motor Torque',
      fieldUnit: 'registry #',
    },
  },
};

// Indices within channels.intScaledVirtual for each VFD channel address field.
export const VFD_CHANNEL_ADDRESS_INDICES: Record<string, number> = {
  vfdAmbientTemperatureAddress: 0,
  vfdDCBusVoltageAddress: 1,
  vfdMotorCurrentAddress: 2,
  vfdMotorVoltageAddress: 3,
  vfdOutFrequencyAddress: 4,
  vfdMotorTorqueAddress: 5,
};

/**
 * Maps virtual form-group names to the WELL_SCHEME keys they aggregate.
 * These fields live at the API root (no nesting), so there is no well['groupName'] object.
 * Add to this map whenever a new set of root-level fields needs its own Angular FormGroup.
 */
export const VIRTUAL_SCHEME_GROUPS: Record<string, string[]> = {
  generalInfo: [
    'allowedUserGroups',
    'dayStartOffset',
    'displayName',
    'name',
    'password',
    'liftType',
    'tags',
    'timeZone',
  ],
};
